import React, { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCustomerPreview } from '../context/CustomerPreviewContext';
import { useAuth } from '../context/AuthContext';
import lazyWithRetry from '../utils/lazyWithRetry';
import { isPlainClick } from './order-workflow/orderLink';
import { ChunkErrorBoundary } from './errors';
import './customers/QuickView.css';

/**
 * The customer quick view: a panel at the right of any page, opened with
 * openCustomerPreview(id) (CustomerPreviewContext) from the customer list,
 * search, the order page, the calendar, Dig, payment history, a Poseidon
 * Plus membership, a boat's owners and Live Calls. Mounted once in
 * AdminLayout. What it shows is customers/QuickView.js, loaded on first open.
 *
 * This shell is the window: its own layer (--z-preview: above the windows it's
 * opened from, under confirms and toasts), a backdrop that closes it, focus
 * moved in on open, kept in while it's open and given back on close, and
 * Escape: it closes the top thing — a confirm, a window the quick view
 * opened (those draw in `layer`, beside the panel), the quick view — and
 * never the page or window under it. Going to another page (a boat, an
 * order, the full profile) closes it.
 */

const QuickView = lazyWithRetry(() => import('./customers/QuickView'));

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const typing = (el) => !!el && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName));

function CustomerPreviewDrawer() {
  const { previewId, closeCustomerPreview } = useCustomerPreview();
  const { can } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const panel = useRef(null);
  const [layer, setLayer] = useState(null);
  const nested = useRef(null); // while a window the quick view opened is up: what Escape does to it
  const returnTo = useRef(null);
  const open = !!previewId;

  // Focus in on open (the element that had it gets it back on close).
  useEffect(() => {
    if (!open) return undefined;
    returnTo.current = document.activeElement;
    panel.current?.focus();
    return () => {
      nested.current = null;
      const back = returnTo.current;
      if (back && document.contains(back) && typeof back.focus === 'function') back.focus();
    };
  }, [open]);

  // Another page: the quick view was for the one being left.
  const openedOn = useRef(pathname);
  useEffect(() => {
    if (open && pathname !== openedOn.current) closeCustomerPreview();
    openedOn.current = pathname;
  }, [open, pathname, closeCustomerPreview]);

  // Escape. While the quick view is up every Escape is its own: the pages and
  // windows under it listen on the document or window (Live Calls' call
  // panel, Dig's drawer, the order page's sidebars), so it's stopped at the
  // page's body on its way up — after the quick view's own fields have had
  // it, before anything under it hears it. Then the top one goes: a confirm
  // (as its Cancel), else a window the quick view opened (its `nested`
  // close), else the quick view itself, unless a field was being typed in
  // (the field's Escape is the field's).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      const confirmOpen = document.querySelector('.confirm-dialog-overlay');
      if (confirmOpen) confirmOpen.click(); // its backdrop cancels it, unless it's busy
      else if (nested.current) nested.current();
      else if (!(typing(e.target) && panel.current?.contains(e.target))) closeCustomerPreview();
    };
    document.body.addEventListener('keydown', onKey);
    return () => document.body.removeEventListener('keydown', onKey);
  }, [open, closeCustomerPreview]);

  // The page under it stays put.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Tab and Shift+Tab go round the panel (a window it opened keeps its own:
  // its keys reach here through React, from outside the panel).
  const keepFocus = (e) => {
    if (e.key !== 'Tab' || !panel.current || !panel.current.contains(e.target)) return;
    const els = [...panel.current.querySelectorAll(FOCUSABLE)];
    if (!els.length) { e.preventDefault(); return; }
    const first = els[0];
    const last = els[els.length - 1];
    const at = document.activeElement;
    if (e.shiftKey && (at === first || at === panel.current)) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && at === last) { e.preventDefault(); first.focus(); }
  };

  // A window the quick view opens: `onEscape` while it's up (null once it's
  // closed, when focus comes back to the panel).
  const onNested = useCallback((onEscape) => {
    const closed = nested.current && !onEscape;
    nested.current = onEscape || null;
    if (closed) panel.current?.focus();
  }, []);

  if (!open) return null;
  const full = `/app/customers/${previewId}`;
  const openFull = (e) => {
    if (!isPlainClick(e)) return; // a new tab: the browser's
    e.preventDefault();
    closeCustomerPreview();
    navigate(full);
  };
  // Leaving for another page: the quick view closes first.
  const go = (path) => { closeCustomerPreview(); navigate(path); };

  return (
    <div className="cqv-root">
      <div className="cqv-backdrop" onMouseDown={closeCustomerPreview} aria-hidden="true" />
      <aside ref={panel} className="cqv-panel" role="dialog" aria-modal="true" aria-labelledby="cqv-title" tabIndex={-1} onKeyDown={keepFocus}>
        <div className="cqv-bar">
          <span className="cqv-bar-title">Customer</span>
          {can('customers.access') && <a className="btn btn-secondary btn-sm" href={full} onClick={openFull}>Open full profile</a>}
          <button type="button" className="cqv-close" onClick={closeCustomerPreview} aria-label="Close">×</button>
        </div>
        <div className="cqv-body">
          {can('customers.access') ? (
            <ChunkErrorBoundary>
              <Suspense fallback={<p className="cqv-wait">Loading the customer…</p>}>
                <QuickView key={previewId} customerId={previewId} layer={layer} onNested={onNested} go={go} onClose={closeCustomerPreview} />
              </Suspense>
            </ChunkErrorBoundary>
          ) : (
            <p id="cqv-title" className="cqv-wait">You don't have permission to view customer details.</p>
          )}
        </div>
      </aside>
      <div ref={setLayer} className="cqv-layer" />
    </div>
  );
}

export default CustomerPreviewDrawer;
